// keyguard-flexible.js
if (typeof window !== "undefined" && !window.__keyguard_installed__) {
  window.__keyguard_installed__ = true;

  // normalize token to canonical key or single-char lowercase
  function normalizeKeyName(name) {
    // map common aliases
    const m = {
      esc: "Escape",
      escape: "Escape",
      del: "Delete",
      delete: "Delete",
      space: " ",
      spacebar: " ",
      enter: "Enter",
      tab: "Tab",
      left: "ArrowLeft",
      right: "ArrowRight",
      up: "ArrowUp",
      down: "ArrowDown",
      backspace: "Backspace",
      capslock: "CapsLock",
      pageup: "PageUp",
      pagedown: "PageDown",
      home: "Home",
      end: "End",
      insert: "Insert",
      pause: "Pause",
    };
    if (!name) return name;
    if (name.length === 1) return name.toLowerCase();
    const low = name.toLowerCase();
    return m[low] ?? name;
  }

  // parse single token like "Ctrl+Shift+S" or "a" or "Enter"
  function parseToken(token) {
    const parts = token.split("+").map(p => p.trim()).filter(Boolean);
    const last = parts.pop();
    const key = normalizeKeyName(last);
    const mods = { ctrl: false, alt: false, shift: false, meta: false };
    for (const p of parts) {
      const lp = p.toLowerCase();
      if (lp === "ctrl" || lp === "control") mods.ctrl = true;
      if (lp === "alt") mods.alt = true;
      if (lp === "shift") mods.shift = true;
      if (lp === "meta" || lp === "cmd" || lp === "super" || lp === "win") mods.meta = true;
    }
    return { key, mods };
  }

  // build allow rules from attribute value
  // returns: null => attribute absent, true => allow all, Array => list of rules
  function parseAllowValue(val) {
    if (val === null) return null;
    if (val.trim() === "") return true; // empty string => allow all
    const tokens = val.split(",").map(t => t.trim()).filter(Boolean);
    return tokens.map(parseToken);
  }

  // read allow info from composed path or closest ancestor
  function getAllowFromPath(e) {
    const path = typeof e.composedPath === "function" ? e.composedPath() : [e.target];
    for (const n of path) {
      if (!(n instanceof Element)) continue;
      if (n.hasAttribute?.("data-allow-hotkeys")) {
        return parseAllowValue(n.getAttribute("data-allow-hotkeys"));
      }
    }
    if (e.target instanceof Element) {
      const closest = e.target.closest?.("[data-allow-hotkeys]");
      if (closest) return parseAllowValue(closest.getAttribute("data-allow-hotkeys"));
    }
    return null;
  }

  // check if event path contains editable field
  function isUserTyping(e) {
    const path = typeof e.composedPath === "function" ? e.composedPath() : [e.target];
    for (const n of path) {
      if (!(n instanceof Element)) continue;
      const tag = (n.tagName || "").toUpperCase();
      if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") {
        if (n.disabled || n.readOnly) continue;
        return true;
      }
      if (n.isContentEditable) return true;
      if (n.getAttribute?.("role") === "textbox") return true;
    }
    return false;
  }

  // match one rule against keyboard event
  function ruleMatchesEvent(rule, e) {
    // modifiers must match exactly
    if (Boolean(e.ctrlKey) !== Boolean(rule.mods.ctrl)) return false;
    if (Boolean(e.altKey) !== Boolean(rule.mods.alt)) return false;
    if (Boolean(e.shiftKey) !== Boolean(rule.mods.shift)) return false;
    if (Boolean(e.metaKey) !== Boolean(rule.mods.meta)) return false;

    // key comparison
    if (typeof rule.key === "string" && rule.key.length === 1) {
      // single char: compare case-insensitive by lowercasing e.key
      const ek = (e.key || "").toLowerCase();
      return ek === rule.key;
    } else {
      // named key: compare exact (ArrowLeft, Enter, Escape, space -> ' ')
      return e.key === rule.key;
    }
  }

  function captureGuard(e) {
    const allow = getAllowFromPath(e);

    // allow all when attribute present and empty
    if (allow === true) return;

    // allow specific keys when allow is set
    if (Array.isArray(allow)) {
      if (isUserTyping(e)) {
        // if any rule matches, permit; else block propagation
        const ok = allow.some(rule => ruleMatchesEvent(rule, e));
        if (!ok) e.stopImmediatePropagation();
      }
      return;
    }

    // backward-compatible Enter allow on legacy jsoneditor marker
    if (e.key === "Enter" && e.target instanceof Element && e.target.getAttribute?.("data-allow-hotkeys") === "jsoneditor") {
      return;
    }

    if (!isUserTyping(e)) return;
    e.stopImmediatePropagation();
  }

  window.addEventListener("keydown", captureGuard, true);
  window.addEventListener("keypress", captureGuard, true);
  window.addEventListener("keyup", captureGuard, true);
}
